<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import every from 'lodash/every';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { useMixins } from './useMixins';
import { usePromosStore } from '@/store/PromosStore';
import { PromosEnum } from '@/store/enums/PromosEnum';
import { useComponentComposable } from '@/components/useComponentComposable';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Toast from 'primevue/toast';
import UploadMultipleImages from './UploadMultipleImages.vue';
import SurfacePromo from '@/components/promos/SurfacePromo.vue';

const { t } = useI18n({ useScope: 'global' });
const { canEditPromo } = usePromoComposable();
const { renderHTMLContent } = useComponentComposable();

const confirm = useConfirm();
const promosStore = usePromosStore();
const toast = useToast();
const isShowError = ref(false);
const previewData = ref(null);
const multipleUploadEn = ref();
const multipleUploadFr = ref();
const visiblePreviewSurface = ref(false);
const headerPreviewSurface = ref({
  headerSurface: '',
  titleHeader: ''
});

const payload: any = reactive({
  promotionType: PromosEnum.promotionType.SURFACE_PROMO,
  titleEn: '',
  titleFr: '',
  subTitleEn: '',
  subTitleFr: '',
  descEn: '',
  descFr: '',
  imgUrlEn: '',
  imgTitleEn: '',
  imgTitleFr: '',
  imgUrlFr: '',
  sk: '',
  nameFirstButtonEn: '',
  urlFirstButtonEn: '',
  nameSecondButtonEn: '',
  urlSecondButtonEn: '',
  nameFirstButtonFr: '',
  urlFirstButtonFr: '',
  nameSecondButtonFr: '',
  urlSecondButtonFr: '',
  imageEn: {
    desktop: '',
    mobile: '',
    tablet: '',
    widescreen: ''
  },
  imageFr: {
    desktop: '',
    mobile: '',
    tablet: '',
    widescreen: ''
  },
  showInB2bEn: false,
  showInB2bFr: false,
  showInB2cEn: false,
  showInB2cFr: false
});
const sectionTitle = reactive({
  surfacePromoTitleEn: '',
  surfacePromoTitleFr: ''
});

const { currentLanguage, v$, previewEn, previewFr } = useMixins(payload);
const objectCopy = ref();
const handleCancel = () => {
  for (const key in payload) {
    payload[key] = objectCopy.value?.[key];
  }
  multipleUploadEn.value.resetFileUpload();
  multipleUploadFr.value.resetFileUpload();
  getSurfacePromos();
};

const handlePreview = (payloadPreview) => {
  previewData.value = payloadPreview;
  window.scrollTo(0, 0);
};
const handleSubmit = async () => {
  const isEmptyPayload = every(omit(cloneDeep(payload), ['sk', 'promotionType', 'qType', 'imageEn', 'imageFr', 'showInB2bEn', 'showInB2bFr', 'showInB2cEn', 'showInB2cFr']), isEmpty);
  if (isEmptyPayload) {
    toast.add({ severity: 'error', summary: '', detail: t('validate.at_least_field_required'), life: 5000 });
    return;
  }
  promosStore.submitPromos(payload, PromosEnum.path.SURFACE_PROMO).then(async () => {
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    isShowError.value = false;
    multipleUploadEn.value.resetFileUpload();
    multipleUploadFr.value.resetFileUpload();
    await getSurfacePromos();
  });
};
const handleDelete = async () => {
  confirm.require({
    message: t('promos.labels.confirm_delete'),
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      promosStore
        .deletePromos(payload.sk, PromosEnum.path.SURFACE_PROMO)
        .then(() => {
          toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
          for (const property in payload) {
            payload[property] = '';
          }
          payload.promotionType = PromosEnum.promotionType.SURFACE_PROMO;
          multipleUploadEn.value.resetFileUpload();
          multipleUploadFr.value.resetFileUpload();
          previewData.value = null;
        })
        .catch((res) => {
          toast.add({ severity: 'error', summary: '', detail: res?.message, life: 5000 });
        });
    }
  });
};
const getSurfacePromos = async () => {
  await promosStore.getSurfacePromos().then((res) => {
    if (res.length > 0) {
      for (const property in payload) {
        payload[property] = res[0][property];
      }
      objectCopy.value = { ...payload };
    }
    handlePreview(currentLanguage === LANG_ENGLISH ? previewEn.value : previewFr.value);
  });
};
const getSectionTitlePromo = () => {
  promosStore.getSectionTitlePromo().then((res) => {
    sectionTitle.surfacePromoTitleEn = res.surfacePromoTitleEn;
    sectionTitle.surfacePromoTitleFr = res.surfacePromoTitleFr;
  });
};
getSectionTitlePromo();
const handleSectionTitleSubmit = async (lang) => {
  const payload = {
    typeEnum: lang === LANG_ENGLISH ? PromosEnum.sectionPromoType.SURFACE_PROMO_TITLE_EN : PromosEnum.sectionPromoType.SURFACE_PROMO_TITLE_FR,
    value: lang === LANG_ENGLISH ? sectionTitle.surfacePromoTitleEn : sectionTitle.surfacePromoTitleFr
  };
  promosStore
    .submitSectionTitlePromos(payload)
    .then(() => {
      getSectionTitlePromo();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};
getSurfacePromos();

const onUploadDataSurfacePromoEn = (dataImage?: any) => {
  payload.imageEn = { ...dataImage };
};

const onUploadDataSurfacePromoFr = (dataImage?: any) => {
  payload.imageFr = { ...dataImage };
};

const handleRemovePreviewImg = (key, lang: string) => {
  if (lang === LANG_ENGLISH) {
    payload.imageEn[key] = '';
    return;
  }
  payload.imageFr[key] = '';
};

const openDialogPreviewSurface = (previewDataSurface, language) => {
  handlePreview(previewDataSurface);
  visiblePreviewSurface.value = true;
  if (language === LANG_ENGLISH) {
    headerPreviewSurface.value = {
      headerSurface: t('promos.labels.preview_english'),
      titleHeader: sectionTitle.surfacePromoTitleEn
    };
    return;
  }
  headerPreviewSurface.value = {
    headerSurface: t('promos.labels.preview_french'),
    titleHeader: sectionTitle.surfacePromoTitleFr
  };
};
</script>

<template>
  <Toast position="top-left" group="tl" />
  <Splitter layout="vertical">
    <SplitterPanel class="mt-5 px-3">
      <Splitter class="section-title">
        <SplitterPanel class="p-3" :size="50">
          <div class="flex flex-col mb-4">
            <p class="text-red-600 text-xl font-bold uppercase">{{ $t('promos.section_title_english') }}</p>
            <InputText :disabled="!canEditPromo" type="text" maxlength="100" :placeholder="$t('promos.section_title_english')" class="mb-2 w-full" v-model.trim="sectionTitle.surfacePromoTitleEn" />
            <Button v-if="canEditPromo" :label="$t('promos.save')" icon="pi pi-check" class="p-button-danger w-fit save-en" @click="handleSectionTitleSubmit(LANG_ENGLISH)" />
          </div>
        </SplitterPanel>
        <SplitterPanel class="p-3" :size="50">
          <div class="flex flex-col mb-4">
            <p class="text-red-600 text-xl font-bold uppercase">{{ $t('promos.section_title_french') }}</p>
            <InputText :disabled="!canEditPromo" type="text" maxlength="100" :placeholder="$t('promos.section_title_french')" class="mb-2 w-full" v-model.trim="sectionTitle.surfacePromoTitleFr" />
            <Button v-if="canEditPromo" :label="$t('promos.save')" icon="pi pi-check" class="p-button-danger w-fit save-fr" @click="handleSectionTitleSubmit('fr')" />
          </div>
        </SplitterPanel>
      </Splitter>
    </SplitterPanel>
    <SplitterPanel class="mt-5 px-3" v-if="previewData">
      <SurfacePromo :value="previewData"></SurfacePromo>
    </SplitterPanel>
    <SplitterPanel>
      <Splitter>
        <SplitterPanel class="p-3 promo-en" :size="50">
          <h5>{{ $t('promos.labels.english') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-titleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.titleEn" />
              <label for="surface-promo-titleEn">{{ $t('promos.labels.title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.titleEn.$invalid">
              {{ v$.titleEn.required.$message }}
            </span>

            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-subTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.subTitleEn" />
              <label for="surface-promo-subTitleEn">{{ $t('promos.labels.subtitle') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.subTitleEn.$invalid">
              {{ v$.subTitleEn.required.$message }}
            </span>
            <span class="p-float-label mt-5">
              <InputTextAera :disabled="!canEditPromo" id="surface-promo-descEn" class="mb-1 w-full" v-model.trim="payload.descEn" :autoResize="true" rows="5" cols="30" />
              <label for="surface-promo-descEn">{{ $t('promos.labels.description') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.descEn.$invalid">
              {{ v$.descEn.required.$message }}
            </span>
            <span class="flex gap-3">
              <span class="p-float-label mt-5 w-4">
                <InputText :disabled="!canEditPromo" id="surface-promo-name-first-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.nameFirstButtonEn" />
                <label for="surface-promo-name-first-button">{{ $t('promos.labels.name_first_button') }}</label>
              </span>
              <span class="p-float-label mt-5 w-8">
                <InputText :disabled="!canEditPromo" id="surface-promo-url-first-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.urlFirstButtonEn" />
                <label for="surface-promo-url-first-button">{{ $t('promos.labels.url_first_button') }}</label>
              </span>
            </span>
            <span class="flex gap-3 w-full">
              <div class="w-3 text-red-500" v-if="isShowError && v$.nameFirstButtonEn.$invalid">{{ v$.nameFirstButtonEn.required.$message }}</div>
              <div class="w-9 text-red-500" v-if="isShowError && v$.urlFirstButtonEn.$invalid">{{ v$.urlFirstButtonEn.required.$message }}</div>
            </span>
            <span class="flex gap-3">
              <span class="p-float-label mt-5 w-4">
                <InputText :disabled="!canEditPromo" id="surface-promo-name-second-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.nameSecondButtonEn" />
                <label for="surface-promo-name-second-button">{{ $t('promos.labels.name_second_button') }}</label>
              </span>
              <span class="p-float-label mt-5 w-8">
                <InputText :disabled="!canEditPromo" id="surface-promo-url-second-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.urlSecondButtonEn" />
                <label for="surface-promo-url-second-button">{{ $t('promos.labels.url_second_button') }}</label>
              </span>
            </span>
            <span class="flex gap-3 w-full">
              <div class="w-3 text-red-500" v-if="isShowError && v$.nameSecondButtonEn.$invalid">{{ v$.nameSecondButtonEn.required.$message }}</div>
              <div class="w-9 text-red-500" v-if="isShowError && v$.urlSecondButtonEn.$invalid">{{ v$.urlSecondButtonEn.required.$message }}</div>
            </span>

            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-imgTitleEn" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleEn" />
              <label for="surface-promo-imgTitleEn">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.imgTitleEn.$invalid">
              {{ v$.imgTitleEn.required.$message }}
            </span>
            <div class="mt-5">
              <UploadMultipleImages
                ref="multipleUploadEn"
                @handleUploadData="(data) => onUploadDataSurfacePromoEn(data)"
                :listUrlPreview="payload.imageEn"
                @handleRemoveImgReview="(key) => handleRemovePreviewImg(key, LANG_ENGLISH)"
              ></UploadMultipleImages>
            </div>
            <Button :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 preview-en" @click="openDialogPreviewSurface(previewEn, LANG_ENGLISH)" />
            <div class="flex gap-3 mt-3 align-items-center w-full justify-content-center">
              <div>
                <Checkbox v-model="payload.showInB2bEn" :binary="true" />
                <label class="ml-1">{{ $t('promos.show_in_B2B') }}</label>
              </div>
              <div>
                <Checkbox v-model="payload.showInB2cEn" :binary="true" />
                <label class="ml-1">{{ $t('promos.show_in_B2C') }}</label>
              </div>
            </div>
          </div>
        </SplitterPanel>
        <SplitterPanel class="p-3 promo-fr" :size="50">
          <h5>{{ $t('promos.labels.french') }}</h5>
          <div class="flex flex-col">
            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-titleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.titleFr" />
              <label for="surface-promo-titleFr">{{ $t('promos.labels.title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.titleFr.$invalid">
              {{ v$.titleFr.required.$message }}
            </span>

            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-subTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.subTitleFr" />
              <label for="surface-promo-subTitleFr">{{ $t('promos.labels.subtitle') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.subTitleFr.$invalid">
              {{ v$.subTitleFr.required.$message }}
            </span>

            <span class="p-float-label mt-5">
              <InputTextAera :disabled="!canEditPromo" id="surface-promo-descFr" class="mb-1 w-full" v-model.trim="payload.descFr" :autoResize="true" rows="5" cols="30" />
              <label for="surface-promo-descFr">{{ $t('promos.labels.description') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.descFr.$invalid">
              {{ v$.descFr.required.$message }}
            </span>

            <span class="flex gap-3">
              <span class="p-float-label mt-5 w-4">
                <InputText :disabled="!canEditPromo" id="surface-promo-name-first-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.nameFirstButtonFr" />
                <label for="surface-promo-name-first-button">{{ $t('promos.labels.name_first_button') }}</label>
              </span>
              <span class="p-float-label mt-5 w-8">
                <InputText :disabled="!canEditPromo" id="surface-promo-url-first-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.urlFirstButtonFr" />
                <label for="surface-promo-url-first-button">{{ $t('promos.labels.url_first_button') }}</label>
              </span>
            </span>
            <span class="flex gap-3 w-full">
              <div class="w-3 text-red-500" v-if="isShowError && v$.nameFirstButtonFr.$invalid">{{ v$.nameFirstButtonFr.required.$message }}</div>
              <div class="w-9 text-red-500" v-if="isShowError && v$.urlFirstButtonFr.$invalid">{{ v$.urlFirstButtonFr.required.$message }}</div>
            </span>
            <span class="flex gap-3">
              <span class="p-float-label mt-5 w-4">
                <InputText :disabled="!canEditPromo" id="surface-promo-name-second-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.nameSecondButtonFr" />
                <label for="surface-promo-name-second-button">{{ $t('promos.labels.name_second_button') }}</label>
              </span>
              <span class="p-float-label mt-5 w-8">
                <InputText :disabled="!canEditPromo" id="surface-promo-url-second-button" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.urlSecondButtonFr" />
                <label for="surface-promo-url-second-button">{{ $t('promos.labels.url_second_button') }}</label>
              </span>
            </span>
            <span class="flex gap-3 w-full">
              <div class="w-3 text-red-500" v-if="isShowError && v$.nameSecondButtonFr.$invalid">{{ v$.nameSecondButtonFr.required.$message }}</div>
              <div class="w-9 text-red-500" v-if="isShowError && v$.urlSecondButtonFr.$invalid">{{ v$.urlSecondButtonFr.required.$message }}</div>
            </span>
            <span class="p-float-label mt-5">
              <InputText :disabled="!canEditPromo" id="surface-promo-imgTitleFr" type="text" maxlength="100" class="mb-1 w-full" v-model.trim="payload.imgTitleFr" />
              <label for="surface-promo-imgTitleFr">{{ $t('promos.labels.image_title') }}</label>
            </span>
            <span class="text-red-500" v-if="isShowError && v$.imgTitleFr.$invalid">
              {{ v$.imgTitleFr.required.$message }}
            </span>
            <div class="mt-5">
              <UploadMultipleImages
                ref="multipleUploadFr"
                @handleUploadData="(data) => onUploadDataSurfacePromoFr(data)"
                :listUrlPreview="payload.imageFr"
                @handleRemoveImgReview="(key) => handleRemovePreviewImg(key, LANG_FRENCH)"
              ></UploadMultipleImages>
            </div>
            <Button :label="$t('promos.labels.preview')" icon="pi pi-eye" class="mt-3 preview-fr" @click="openDialogPreviewSurface(previewFr, LANG_FRENCH)" />
            <div class="flex gap-3 mt-3 align-items-center w-full justify-content-center">
              <div>
                <Checkbox v-model="payload.showInB2bFr" :binary="true" />
                <label class="ml-1">{{ $t('promos.show_in_B2B') }}</label>
              </div>
              <div>
                <Checkbox v-model="payload.showInB2cFr" :binary="true" />
                <label class="ml-1">{{ $t('promos.show_in_B2C') }}</label>
              </div>
            </div>
          </div>
        </SplitterPanel>
      </Splitter>
    </SplitterPanel>
    <SplitterPanel v-if="canEditPromo" class="p-3 text-center justify-content-center">
      <Button :label="$t('promos.save')" icon="pi pi-check" class="p-button-secondary mr-2 btn-submit" @click="handleSubmit()" />
      <Button :disabled="!payload.sk" :label="$t('promos.delete')" icon="pi pi-trash" class="mr-2 btn-delete" @click="handleDelete()" />
      <Button :label="$t('promos.labels.cancel')" icon="pi pi-times" class="btn-cancel" @click="handleCancel()" />
    </SplitterPanel>
  </Splitter>
  <Dialog :header="headerPreviewSurface.headerSurface" v-model:visible="visiblePreviewSurface" :modal="true" class="w-7">
    <renderHTMLContent :data="headerPreviewSurface.titleHeader" htmlElement="h2"></renderHTMLContent>
    <SurfacePromo :value="previewData"></SurfacePromo>
  </Dialog>
</template>

<style lang="scss" scoped>
.p-button.p-component {
  padding: 0.45rem 0.95rem;
}
</style>
